import { useEffect, useState, type CSSProperties } from 'react'
import {
  cancelResidentGuestSession,
  checkPhoneOtp,
  getResidentAllowance,
  getResidentGuestInfo,
  lookupResidentGuest,
  startPhoneOtp,
  startResidentGuestSession,
  CONSUMER_URL,
  type ResidentAllowance,
  type ResidentGuestInfo,
  type ResidentGuestRecord,
  type ResidentGuestStartResult,
} from '../lib/api'
import { Turnstile, isTurnstileConfigured } from '../components/Turnstile'
import { useOtpTurnstile } from '../components/useOtpTurnstile'
import { CHALLENGE_COPY, sendWithTurnstile } from '../lib/otpTurnstile'
import { labelStyle as baseLabel, purpleBtn } from '../ui'

const pageBackground = '#FCFBFD'
const line = 'var(--color-gray-200)'
const input: CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '11px 13px',
  border: '1.5px solid var(--color-gray-300)', borderRadius: 10,
  fontSize: 16, fontFamily: 'inherit', background: '#fff',
}
const fieldLabel: CSSProperties = { ...baseLabel, display: 'block', marginBottom: 6 }
const card: CSSProperties = { background: '#fff', border: `1px solid ${line}`, borderRadius: 14 }
const guestButton: CSSProperties = { ...purpleBtn, padding: '10px 15px' }

function minutesLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  if (!hours) return `${remainder}m`
  if (!remainder) return `${hours}h`
  return `${hours}h ${remainder}m`
}

function periodLabel(allowance: ResidentAllowance): string {
  return allowance.mode === 'calendar_week' ? 'Calendar week' : 'Rolling seven days'
}

function Stat({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <div style={{ ...card, flex: 1, padding: '13px 14px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: tone ?? 'var(--color-gray-900)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--color-gray-600)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6 }}>{label}</div>
    </div>
  )
}

export function ResidentGuest({ tenantId }: { tenantId: string }) {
  const [info, setInfo] = useState<ResidentGuestInfo | null>(null)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  // Every /phone/start goes through `sendWithTurnstile`: it waits for a fresh,
  // single-use token instead of sending whatever is in state at tap time, and
  // retries once on `turnstile_required` (lib/otpTurnstile.ts).
  const { gate: turnstile, widget: turnstileWidget } = useOtpTurnstile(isTurnstileConfigured())
  // Set only while Cloudflare shows a challenge the resident has to tap.
  const [challengeLine, setChallengeLine] = useState('')
  const [record, setRecord] = useState<ResidentGuestRecord | null>(null)
  const [allowance, setAllowance] = useState<ResidentAllowance | null>(null)
  const [selectedPlate, setSelectedPlate] = useState('')
  const [requestedMinutes, setRequestedMinutes] = useState(120)
  const [started, setStarted] = useState<ResidentGuestStartResult | null>(null)
  const [residentToken, setResidentToken] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void getResidentGuestInfo(tenantId)
      .then(setInfo)
      .catch(() => setError('This guest parking link is not valid.'))
  }, [tenantId])

  const accent = info?.brandColor && /^#[0-9a-fA-F]{6}$/.test(info.brandColor) ? info.brandColor : '#7F3F98'

  const send = async () => {
    setBusy(true); setError('')
    try {
      await sendWithTurnstile(turnstile, (t) => startPhoneOtp(phone, t), (w) => {
        setChallengeLine(w === 'challenge' ? CHALLENGE_COPY : '')
      })
      setSent(true)
    }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send a code.') }
    finally {
      setChallengeLine('')
      setBusy(false)
    }
  }

  const verify = async () => {
    setBusy(true); setError('')
    try {
      const auth = await checkPhoneOtp(phone, code)
      const nextRecord = await lookupResidentGuest(auth.token, tenantId)
      setRecord(nextRecord)
      setSelectedPlate(nextRecord.plates[0] ?? '')
      setAllowance(await getResidentAllowance(auth.token, tenantId))
      setResidentToken(auth.token)
    } catch (e) { setError(e instanceof Error ? e.message : 'That code could not be verified.') }
    finally { setBusy(false) }
  }

  const start = async () => {
    if (!record || !allowance || allowance.remainingMinutes <= 0 || !selectedPlate) return
    if (!residentToken) { setError('Please verify your phone again.'); return }
    setBusy(true); setError(''); setNotice('')
    try {
      setStarted(await startResidentGuestSession(residentToken, {
        tenantId, plate: selectedPlate, durationMinutes: requestedMinutes, requestId: crypto.randomUUID(),
      }))
      setAllowance(await getResidentAllowance(residentToken, tenantId))
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not start guest parking.') }
    finally { setBusy(false) }
  }

  const cancel = async () => {
    if (!started || !residentToken) return
    setCancelling(true); setError(''); setNotice('')
    try {
      const result = await cancelResidentGuestSession(residentToken, { tenantId, txnId: started.txnId })
      setStarted(null)
      setAllowance(await getResidentAllowance(residentToken, tenantId))
      setNotice(result.alreadyCanceled ? 'This guest session was already canceled.' : `${result.restoredMinutes} unused minutes were returned to your allowance.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not cancel guest parking.') }
    finally { setCancelling(false) }
  }

  const paidHref = info?.defaultGuestZoneId
    ? `${CONSUMER_URL}/?z=${encodeURIComponent(info.defaultGuestZoneId)}${selectedPlate ? `&plate=${encodeURIComponent(selectedPlate)}` : ''}`
    : CONSUMER_URL

  if (!info && !error) return <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>
  if (!info) return <div style={{ padding: 40, textAlign: 'center' }}>{error}</div>

  return (
    <div style={{ minHeight: '100vh', background: pageBackground }}>
      <header style={{ background: '#fff', borderBottom: `1px solid ${line}`, padding: '14px 20px' }}>
        <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 20, width: 'auto', display: 'block' }} />
      </header>
      <main style={{ maxWidth: 460, margin: '0 auto', padding: '22px 20px 26px' }}>
        <div style={{ ...baseLabel, marginBottom: 5 }}>Building</div>
        <div style={{ color: accent, fontWeight: 800, fontSize: 15, marginBottom: 16 }}>{info.name}</div>
        <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.4px', margin: '0 0 10px' }}>Guest parking</h1>
        <div style={{ display: 'inline-block', background: '#F2E9F6', color: accent, borderRadius: 20, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 24 }}>
          Guest parking · {info.weeklyGuestHours} hours per apartment each week
        </div>
        {error && <div role="alert" style={{ margin: '0 0 14px', color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        {notice && <div role="status" style={{ margin: '0 0 14px', color: '#15803d', fontSize: 13, fontWeight: 700 }}>{notice}</div>}

        {!record ? (
          <>
            <p style={{ color: 'var(--color-gray-600)', fontSize: 13, fontWeight: 600, lineHeight: 1.6, margin: '0 0 16px' }}>
              Enter your phone number to see your PIN and what is left this week.
            </p>
            <label style={fieldLabel} htmlFor="resident-phone">Mobile number</label>
            <input id="resident-phone" style={input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(704) 555-0100" inputMode="tel" autoComplete="tel" />
            {sent && <><label style={{ ...fieldLabel, marginTop: 16 }} htmlFor="resident-code">SMS code</label><input id="resident-code" style={input} value={code} onChange={e => setCode(e.target.value)} placeholder="Enter code" inputMode="numeric" autoComplete="one-time-code" /></>}
            {challengeLine && <div role="status" style={{ margin: '12px 0 0', color: 'var(--color-gray-700)', fontSize: 13, fontWeight: 600 }}>{challengeLine}</div>}
            {!sent && <Turnstile {...turnstileWidget} />}
            <button type="button" disabled={busy} onClick={() => void (sent ? verify() : send())} style={{ ...guestButton, width: '100%', marginTop: 6, opacity: busy ? 0.65 : 1 }}>
              {busy ? 'Working…' : sent ? 'Verify phone' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <div style={{ ...card, padding: 18, background: '#F2E9F6', borderColor: 'transparent', textAlign: 'center', marginTop: 6 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: accent, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>Your guest PIN</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: accent, letterSpacing: '0.18em', lineHeight: 1.05 }}>{record.guestPin}</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: accent, marginTop: 10, lineHeight: 1.5 }}>Your guest enters this at checkout. It covers their parking out of your weekly hours.</div>
            </div>
            {allowance && <>
              <div style={{ display: 'flex', gap: 9, marginTop: 14 }}>
                <Stat value={minutesLabel(allowance.remainingMinutes)} label="Left this week" tone={allowance.remainingMinutes <= 0 ? '#b91c1c' : accent} />
                <Stat value={minutesLabel(allowance.usedMinutes)} label="Used this week" />
              </div>
              <div style={{ ...card, padding: '13px 15px', marginTop: 14 }}>
                <div style={{ ...baseLabel, marginBottom: 5 }}>Resets</div>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--color-gray-900)', marginBottom: 6 }}>{periodLabel(allowance)}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>Active guest sessions count immediately. Unused time does not carry over.</div>
              </div>
              {allowance.remainingMinutes <= 0 && <div style={{ ...card, background: '#FFF7F5', borderColor: '#F0B3AB', padding: '14px 15px', marginTop: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 5 }}>No guest hours left this week</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>Guests can still park and pay for themselves.</div>
                <a href={paidHref} style={{ display: 'inline-block', marginTop: 10, color: accent, fontWeight: 800, fontSize: 13 }}>Continue to paid parking →</a>
              </div>}
            </>}
            {!started && allowance && allowance.remainingMinutes > 0 && <div style={{ ...card, padding: '18px 15px', marginTop: 14 }}>
              <label style={fieldLabel} htmlFor="resident-plate">Plate</label>
              {record.plates.length > 1 ? <select id="resident-plate" value={selectedPlate} onChange={e => setSelectedPlate(e.target.value)} style={{ ...input, display: 'block' }}>{record.plates.map(plate => <option key={plate}>{plate}</option>)}</select> : <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6 }}>{record.plates[0]}</div>}
              <label style={{ ...fieldLabel, marginTop: 16 }} htmlFor="resident-duration">How long</label>
              <select id="resident-duration" value={requestedMinutes} onChange={e => setRequestedMinutes(Number(e.target.value))} style={{ ...input, display: 'block' }}><option value={30}>30 minutes</option><option value={60}>1 hour</option><option value={120}>2 hours</option><option value={240}>4 hours</option></select>
              <button type="button" disabled={busy || !selectedPlate} onClick={() => void start()} style={{ ...guestButton, width: '100%', marginTop: 18, opacity: busy ? 0.65 : 1 }}>{busy ? 'Starting…' : 'Start guest parking'}</button>
            </div>}
            {started && <div role="status" style={{ ...card, padding: '18px 15px', marginTop: 14 }}>
              <div style={{ color: '#15803d', fontWeight: 800 }}>Guest parking is active.</div>
              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>{started.clamped ? `Started ${minutesLabel(started.minutesStarted)} — your request was capped at the remaining allowance.` : `Started for ${minutesLabel(started.minutesStarted)}.`}</div>
              <div style={{ marginTop: 6, color: 'var(--color-gray-600)', fontSize: 12 }}>Plate {started.plate} · ends {new Date(started.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
              <button type="button" disabled={cancelling} onClick={() => void cancel()} style={{ ...guestButton, background: '#fff', color: 'var(--color-gray-700)', border: `1px solid ${line}`, marginTop: 16, opacity: cancelling ? 0.65 : 1 }}>{cancelling ? 'Canceling…' : 'Cancel guest parking'}</button>
            </div>}
          </>
        )}
      </main>
    </div>
  )
}
